import { Modal, Form } from "react-bootstrap";
import { useEffect, useState } from "react";
import { InfoCircleFill } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";

type Props = {
  show: boolean;
  onClose: () => void;
  onConfirm: (groupId: number | null) => void;

  parkerName: string;
  currentGroupId?: number | null;
  groups: { id: number; name: string }[];
};

export function ChangeParkerGroupModal({
  show,
  onClose,
  onConfirm,
  parkerName,
  currentGroupId,
  groups,
}: Props) {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(
    currentGroupId ?? null
  );
  const [showBillingConfirm, setShowBillingConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedGroupId(currentGroupId ?? null);
      setShowBillingConfirm(false);
    }
  }, [show, currentGroupId]);

  const isChange = currentGroupId != null;
  const selectedName =
    selectedGroupId != null
      ? (groups.find(g => g.id === selectedGroupId)?.name ?? "selected group")
      : "";

  const handleClose = () => {
    setShowBillingConfirm(false);
    onClose();
  };

  const handlePrimaryClick = () => {
    if (!selectedGroupId) return;
    setShowBillingConfirm(true);
  };

  const handleConfirmBilling = () => {
    onConfirm(selectedGroupId);
    setShowBillingConfirm(false);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <HeaderText>
          {showBillingConfirm
            ? "Confirm group change"
            : isChange
              ? "Change Group"
              : "Assign to Group"}
        </HeaderText>
      </Modal.Header>

      <Modal.Body>
        {showBillingConfirm ? (
          <>
            <p className="mb-3">
              Move <strong>{parkerName}</strong> to <strong>{selectedName}</strong>?
            </p>
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
              <InfoCircleFill className="flex-shrink-0 mt-1" />
              <span>
                This group change will be reflected in the next billing period.
              </span>
            </div>
          </>
        ) : (
          <>
            <p className="mb-3">
              <strong>{parkerName}</strong>
            </p>

            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Select
                value={selectedGroupId ?? ""}
                onChange={(e) =>
                  setSelectedGroupId(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">Select a group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {showBillingConfirm ? (
          <>
            <Button onClick={() => setShowBillingConfirm(false)}>Back</Button>
            <Button className="btn-main" onClick={handleConfirmBilling}>
              Confirm
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              className="btn-main"
              disabled={!selectedGroupId || selectedGroupId === currentGroupId}
              onClick={handlePrimaryClick}
            >
              {isChange ? "Change Group" : "Assign to Group"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
